/**
 * Attributes & groups — the model and the rule engine (S3).
 *
 * Pure functions, no React and no database, so the same rules drive the design
 * prototype now and the real screens later — and so the database version can be
 * checked against these tests. Decisions are in docs/product/ATTRIBUTES_UI_DESIGN_BRIEF.md (v2):
 *   - within one condition several values mean ANY OF; between conditions ALL must hold;
 *   - no "is empty" / "is not empty" conditions (6 Oct 2026). Blanks are handled two ways:
 *       · "Not set" is a value you can pick in "is any of" / "has any of";
 *       · "is not" / "has none of" leave out members with no value, unless the condition's
 *         "Also include people with no …" box is ticked (`withEmpty`) — and the words say so;
 *   - deactivated members are never in a group or a count;
 *   - an archived option keeps working in a rule and is shown "(archived)".
 */

export type AttrType =
  | 'text' | 'number' | 'date' | 'option' | 'multi' | 'yesno'   // custom types
  | 'person' | 'location' | 'language' | 'phone'               // standard-only types

export const CUSTOM_TYPES: AttrType[] = ['text', 'number', 'date', 'option', 'multi', 'yesno']
export const MAX_CUSTOM = 6

export const TYPE_LABEL: Record<AttrType, string> = {
  text: 'Text', number: 'Number', date: 'Date', option: 'Option', multi: 'Multi-option', yesno: 'Yes / No',
  person: 'Person', location: 'Location', language: 'Language', phone: 'Phone',
}
export const TYPE_EXAMPLE: Record<AttrType, string> = {
  text: 'Store code, Employee ID', number: 'Years of experience, Target', date: 'Certified on, Contract end',
  option: 'Region, Brand — one value from a list', multi: 'Products sold — several from a list', yesno: 'First-aid trained',
  person: 'Picked from eligible members', location: 'Country · State · City', language: 'Preferred language', phone: 'Mobile number',
}

export type PersonList = 'manager' | 'trainer' | 'coach'
export interface Opt { id: string; label: string; archived?: boolean }
export interface AttrDef {
  key: string
  label: string
  type: AttrType
  standard: boolean
  required: boolean
  unique?: boolean
  options?: Opt[]                 // option / multi
  personList?: PersonList         // person
  memberEdit?: boolean            // the member may edit their own value
  /** Standard attributes whose "Who can edit" is decided by Learnbee, not the admin (5 Oct 2026):
   *  'member' = always the member too (Mobile, Date of birth, Language);
   *  'admins' = never the member (Manager / Trainer / Coach — reporting lines).
   *  Absent = the admin's choice (Designation, Hire date, Location — on for the member by default
   *  since 5 Oct 2026 — and every custom one). */
  editLock?: 'member' | 'admins'
  memberVisible?: boolean         // the member sees it on their profile
  fromAccount?: boolean           // first / last name: comes from their sign-in account, not managed here
  archived?: boolean
}

export interface LocationValue { country: string; state?: string; city?: string }

/** A group: 'auto' = attribute group (members worked out from `rule`); 'manual' = user group. */
export interface Group {
  id: string
  name: string
  kind: 'auto' | 'manual'
  rule: Condition[]          // auto
  members: string[]          // manual (person ids)
  archived?: boolean
  /** When it was made and last changed (14 Oct 2026 — the user groups pages show them). */
  createdAt?: string
  updatedAt?: string
}
export type Value = string | number | boolean | string[] | LocationValue | null | undefined

export interface Person { id: string; name: string; email: string; active: boolean; values: Record<string, Value> }

// ─── Conditions ──────────────────────────────────────────────────────────────

export type Op =
  | 'any_of' | 'not'                                  // option, person, language, location
  | 'has_any' | 'has_all' | 'has_none'                 // multi
  | 'is' | 'contains'                                  // text
  | 'eq' | 'gt' | 'lt' | 'between'                     // number
  | 'last_days' | 'before' | 'after' | 'date_between'  // date
  | 'tenure_more' | 'tenure_less'                      // hire date → tenure (months)
  | 'yes' | 'no'                                       // yes / no

export type LocationLevel = 'country' | 'state' | 'city'

export interface Condition {
  attr: string
  op: Op
  values?: string[]          // option ids, person ids, language codes, location names, text
  n?: number                 // number / days / months
  m?: number                 // upper bound for between
  from?: string              // ISO date
  to?: string
  level?: LocationLevel      // location conditions
  withEmpty?: boolean        // "is not" / "has none of": also include members with no value
}

/** The value id for "no value" in a value list — "Region is South or Not set". */
export const NOT_SET = '__not_set__'

/** The operators that make sense for a type, in the order the builder lists them. */
export function opsFor(def: AttrDef): Array<{ op: Op; label: string }> {
  switch (def.type) {
    case 'option': case 'language': case 'location':
      return [{ op: 'any_of', label: 'is any of' }, { op: 'not', label: 'is not' }]
    case 'person':
      return [{ op: 'any_of', label: 'is' }, { op: 'not', label: 'is not' }]
    case 'multi':
      return [{ op: 'has_any', label: 'has any of' }, { op: 'has_all', label: 'has all of' }, { op: 'has_none', label: 'has none of' }]
    case 'text': case 'phone':
      return [{ op: 'is', label: 'is' }, { op: 'contains', label: 'contains' }]
    case 'number':
      return [{ op: 'eq', label: 'is' }, { op: 'gt', label: 'is more than' }, { op: 'lt', label: 'is less than' }, { op: 'between', label: 'is between' }]
    case 'date': {
      const base: Array<{ op: Op; label: string }> = [{ op: 'last_days', label: 'is in the last' }, { op: 'before', label: 'is before' }, { op: 'after', label: 'is after' }, { op: 'date_between', label: 'is between' }]
      return def.key === 'hire_date'
        ? [...base, { op: 'tenure_more', label: 'tenure is more than' }, { op: 'tenure_less', label: 'tenure is less than' }]
        : base
    }
    case 'yesno':
      return [{ op: 'yes', label: 'is yes' }, { op: 'no', label: 'is no' }]
  }
}

// ─── Evaluation ──────────────────────────────────────────────────────────────

const isEmpty = (v: Value) =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)

const DAY = 86_400_000
/** Whole months between a date and today (tenure). */
export function monthsSince(iso: string, now = new Date()): number {
  const d = new Date(iso)
  let m = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth())
  if (now.getDate() < d.getDate()) m -= 1
  return Math.max(0, m)
}

/** The value a condition compares — a location condition reads one level of it. */
function scalarFor(c: Condition, v: Value): string | undefined {
  if (isEmpty(v)) return undefined
  if (typeof v === 'object' && !Array.isArray(v)) {
    const loc = v as LocationValue
    const lvl = c.level ?? 'country'
    // A city is identified with its state so same-named cities don't collide.
    return lvl === 'city' ? (loc.city ? `${loc.city}|${loc.state ?? ''}` : undefined) : (lvl === 'state' ? loc.state : loc.country)
  }
  return String(v)
}

export function matchesCondition(p: Person, c: Condition, now = new Date()): boolean {
  const v = p.values[c.attr]
  const vals = c.values ?? []
  switch (c.op) {
    case 'any_of': { const s = scalarFor(c, v); return s === undefined ? vals.includes(NOT_SET) : vals.includes(s) }
    case 'not': { const s = scalarFor(c, v); return s === undefined ? !!c.withEmpty : !vals.includes(s) }
    case 'has_any': return isEmpty(v) ? vals.includes(NOT_SET) : Array.isArray(v) && v.some(x => vals.includes(x))
    case 'has_all': return Array.isArray(v) && vals.every(x => v.includes(x))
    case 'has_none': return isEmpty(v) || !Array.isArray(v) ? !!c.withEmpty : !v.some(x => vals.includes(x))
    case 'is': return !isEmpty(v) && String(v).trim().toLowerCase() === (vals[0] ?? '').trim().toLowerCase()
    case 'contains': return !isEmpty(v) && String(v).toLowerCase().includes((vals[0] ?? '').toLowerCase())
    case 'eq': return typeof v === 'number' && v === c.n
    case 'gt': return typeof v === 'number' && c.n !== undefined && v > c.n
    case 'lt': return typeof v === 'number' && c.n !== undefined && v < c.n
    case 'between': return typeof v === 'number' && c.n !== undefined && c.m !== undefined && v >= Math.min(c.n, c.m) && v <= Math.max(c.n, c.m)
    case 'last_days': return typeof v === 'string' && c.n !== undefined && now.getTime() - new Date(v).getTime() <= c.n * DAY && new Date(v).getTime() <= now.getTime()
    case 'before': return typeof v === 'string' && !!c.from && v < c.from
    case 'after': return typeof v === 'string' && !!c.from && v > c.from
    case 'date_between': return typeof v === 'string' && !!c.from && !!c.to && v >= c.from && v <= c.to
    case 'tenure_more': return typeof v === 'string' && c.n !== undefined && monthsSince(v, now) > c.n
    case 'tenure_less': return typeof v === 'string' && c.n !== undefined && monthsSince(v, now) < c.n
    case 'yes': return v === true
    case 'no': return v === false
  }
}

/** Everyone ACTIVE who meets every condition. No conditions → every active member. */
export function evaluate(people: Person[], rule: Condition[], now = new Date()): Person[] {
  return people.filter(p => p.active && rule.every(c => matchesCondition(p, c, now)))
}

/** A condition is complete when it has what its operator needs. */
export function isComplete(c: Condition): boolean {
  switch (c.op) {
    case 'yes': case 'no': return true
    case 'eq': case 'gt': case 'lt': case 'last_days': case 'tenure_more': case 'tenure_less': return c.n !== undefined && !Number.isNaN(c.n)
    case 'between': return c.n !== undefined && c.m !== undefined
    case 'before': case 'after': return !!c.from
    case 'date_between': return !!c.from && !!c.to
    default: return (c.values ?? []).some(x => x.trim() !== '')
  }
}

// ─── Plain words ─────────────────────────────────────────────────────────────

export interface Lookup { optionLabel(attr: string, id: string): { label: string; archived: boolean }; personName(id: string): string }

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '…')
const LEVEL_LABEL: Record<LocationLevel, string> = { country: 'Country', state: 'State', city: 'City' }

/** One condition in words; archived values are flagged so the UI can mute them. */
export function describeCondition(c: Condition, def: AttrDef | undefined, look: Lookup): { text: string; archivedValues: string[] } {
  const name = def ? (def.type === 'location' ? LEVEL_LABEL[c.level ?? 'country'] : def.label) : c.attr
  const archivedValues: string[] = []
  const valueWords = (c.values ?? []).map(id => {
    if (id === NOT_SET) return 'not set'
    if (def?.type === 'option' || def?.type === 'multi') {
      const o = look.optionLabel(c.attr, id)
      if (o.archived) archivedValues.push(o.label)
      return o.archived ? `${o.label} (archived)` : o.label
    }
    if (def?.type === 'person') return look.personName(id)
    if (def?.type === 'location' && (c.level ?? 'country') === 'city') { const [city, state] = id.split('|'); return state ? `${city} (${state})` : city }
    return id
  })
  const list = (joiner: 'or' | 'and') => valueWords.length <= 1 ? (valueWords[0] ?? '…') : `${valueWords.slice(0, -1).join(', ')} ${joiner} ${valueWords[valueWords.length - 1]}`
  const lowerName = name.charAt(0).toLowerCase() + name.slice(1)
  const noValue = c.withEmpty ? ` (or no ${lowerName})` : ''
  let text: string
  switch (c.op) {
    case 'any_of': text = `${name} is ${list('or')}`; break
    case 'not': text = `${name} is not ${list('or')}${noValue}`; break
    case 'has_any': text = `${name} has ${list('or')}`; break
    case 'has_all': text = `${name} has ${list('and')}`; break
    case 'has_none': text = `${name} has none of ${list('or')}${noValue}`; break
    case 'is': text = `${name} is “${valueWords[0] ?? '…'}”`; break
    case 'contains': text = `${name} contains “${valueWords[0] ?? '…'}”`; break
    case 'eq': text = `${name} is ${c.n ?? '…'}`; break
    case 'gt': text = `${name} is more than ${c.n ?? '…'}`; break
    case 'lt': text = `${name} is less than ${c.n ?? '…'}`; break
    case 'between': text = `${name} is between ${c.n ?? '…'} and ${c.m ?? '…'}`; break
    case 'last_days': text = `${name} is in the last ${c.n ?? '…'} days`; break
    case 'before': text = `${name} is before ${fmtDate(c.from)}`; break
    case 'after': text = `${name} is after ${fmtDate(c.from)}`; break
    case 'date_between': text = `${name} is between ${fmtDate(c.from)} and ${fmtDate(c.to)}`; break
    case 'tenure_more': text = `Tenure is more than ${c.n ?? '…'} months`; break
    case 'tenure_less': text = `Tenure is less than ${c.n ?? '…'} months`; break
    case 'yes': text = `${name} is yes`; break
    case 'no': text = `${name} is no`; break
  }
  return { text, archivedValues }
}

/** Whether a member has a value — the "42 of 50 filled in" figure counts active members only. */
export function filledCount(people: Person[], key: string): { filled: number; total: number } {
  const active = people.filter(p => p.active)
  return { filled: active.filter(p => !isEmpty(p.values[key])).length, total: active.length }
}

/**
 * What is wrong with a date value, or null when it is fine (22 Oct 2026 — a cell accepted
 * "12 Sept 200"). Every date must be a real calendar day between 1900 and 2100; a date of birth
 * also cannot be in the future. Shared by the table, the profile, invites, CSV and the server.
 */
export const DATE_MIN = '1900-01-01'
export const DATE_MAX = '2100-12-31'
export function dateMax(def: Pick<AttrDef, 'key'>, now = new Date()): string {
  return def.key === 'dob' ? localIso(now) : DATE_MAX
}
export function dateProblem(def: Pick<AttrDef, 'key' | 'label'>, v: string, now = new Date()): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return `${def.label}: write the date as YYYY-MM-DD.`
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
  if (d.getUTCMonth() !== +m[2] - 1 || d.getUTCDate() !== +m[3]) return `${def.label}: pick a real date.`
  if (v < DATE_MIN) return `${def.label}: the year must be 1900 or later.`
  if (v > dateMax(def, now)) return def.key === 'dob' ? `${def.label} can’t be in the future.` : `${def.label}: the year must be 2100 or earlier.`
  return null
}
/** The first date problem among a set of values (keyed by attribute key), or null — for forms that save several at once. */
export function valuesProblem(defs: Pick<AttrDef, 'key' | 'label' | 'type'>[], values: Record<string, Value>, now = new Date()): string | null {
  for (const d of defs) {
    const v = values[d.key]
    if (d.type === 'date' && typeof v === 'string' && v) { const why = dateProblem(d, v, now); if (why) return why }
  }
  return null
}
function localIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** A short display of a member's value. */
export function displayValue(def: AttrDef, v: Value, personName: (id: string) => string, now = new Date()): string {
  if (isEmpty(v)) return '—'
  switch (def.type) {
    case 'option': return def.options?.find(o => o.id === v)?.label ?? String(v)
    case 'multi': return (v as string[]).map(id => def.options?.find(o => o.id === id)?.label ?? id).join(', ')
    case 'yesno': return v ? 'Yes' : 'No'
    case 'person': return personName(String(v))
    case 'location': { const l = v as LocationValue; return [l.country, l.state, l.city].filter(Boolean).join(' · ') }
    case 'date': {
      const s = fmtDate(String(v))
      if (def.key !== 'hire_date') return s
      const m = monthsSince(String(v), now)
      return `${s} (${m >= 12 ? `${Math.floor(m / 12)} yr${m >= 24 ? 's' : ''}${m % 12 ? ` ${m % 12} mo` : ''}` : `${m} month${m === 1 ? '' : 's'}`})`
    }
    default: return String(v)
  }
}

/** Keep a page index valid as the list it pages shrinks. */
export function pageOf<X>(items: X[], page: number, size: number) {
  const at = Math.min(page, Math.max(0, Math.ceil(items.length / size) - 1))
  return { at, rows: items.slice(at * size, (at + 1) * size) }
}

/** Where the standard attributes go (5 Oct 2026) — the admin's Attributes page and the member's
 *  profile use the same layout, three to a row with no sub-headings:
 *    Basic information   First name · Last name · Email / Mobile · Date of birth · Role
 *    Standard            Designation · Hire date · Preferred language
 *                        Country · State · City   (Location, as three linked fields)
 *                        Manager · Trainer · Coach */
export const BASIC_KEYS = ['first_name', 'last_name', 'mobile', 'dob']
export const STANDARD_ORDER = ['job_title', 'hire_date', 'language', 'location', 'manager', 'trainer', 'coach']
